/**
 * Пункт «Оприлюднити на порталі» — у меню віджета й у «Експорт як…», поруч із CSV, XLSX, JSON і
 * GeoJSON (запит власника 2026-10-01). Портал — той, що обрано для набору таблиці в адмінці: data.gov.ua
 * або місцевий портал громади, тож у підписі його не названо. [[publikatsiia-na-mistsevyi-portal-hromady]]
 *
 * Це та сама публікація, що й «Опублікувати зараз» в адмінці, але лише цієї таблиці. Куди саме
 * публікувати — розпорядник, набір, ресурс — налаштовується в адмінці («Публікація»). Якщо щось не
 * налаштовано, сервер так і скаже, і повідомлення пояснить, що робити.
 *
 * Пункт бачать головний адмін і адміни громад (`/whoami`; адмін області — ні, він лише переглядає),
 * і лише на таблицях наборів
 * (`S<група>_<ресурс>`). Це не захист: межу прав тримає automation за ідентичністю, яку бере сервер
 * Grist. [[publikatsiia-z-storinky-tablytsi]]
 */
import { GristDoc } from "app/client/components/GristDoc";
import { regionalPublishAccess } from "app/client/lib/RegionalAdminAccess";
import { makeT } from "app/client/lib/localization";
import { ViewSectionRec } from "app/client/models/DocModel";
import { reportError, reportSuccess } from "app/client/models/errors";
import { testId } from "app/client/ui2018/cssVars";
import { menuItem } from "app/client/ui2018/menus";
import { confirmModal } from "app/client/ui2018/modals";

import { dom, DomContents, DomElementArg } from "grainjs";

const t = makeT("RegionalPublish");

/** Таблиці наборів Рекомендацій; зведені та службові таблиці на портал не йдуть. */
const DATASET_TABLE = /^S\d+_[A-Za-z0-9]+$/;

function sectionTableId(viewSection: ViewSectionRec | null | undefined): string | null {
  const tableId = viewSection?.table.peek()?.tableId.peek();
  return tableId && DATASET_TABLE.test(tableId) ? tableId : null;
}

export function publishMenuItems(
  gristDoc: GristDoc,
  viewSection: ViewSectionRec | null | undefined,
  ...args: DomElementArg[]
): DomContents[] {
  const tableId = sectionTableId(viewSection);
  if (!tableId) {
    return [];
  }
  const allowed = regionalPublishAccess(null);
  return [dom.maybe(allowed, () => menuItem(
    () => confirmPublish(gristDoc, tableId),
    ...args,
    t("Publish to the open data portal"),
    testId("regional-publish"),
  ))];
}

function confirmPublish(gristDoc: GristDoc, tableId: string) {
  confirmModal(
    t("Publish the table on the open data portal?"),
    t("Publish"),
    () => publishTable(gristDoc, tableId),
    {
      explanation: dom("div", t(
        "The table data will be uploaded to the portal resource set up in the admin panel (Publication): " +
        "data.gov.ua or the community's own portal. " +
        "A new dataset on data.gov.ua becomes public after moderation by Diia.",
      )),
    },
  );
}

async function publishTable(gristDoc: GristDoc, tableId: string) {
  const docId = gristDoc.docPageModel.currentDocId.get();
  if (!docId) {
    return;
  }
  try {
    const result = await gristDoc.app.topAppModel.api.getDocAPI(docId).publishRegionalTable(tableId);
    reportSuccess(result.message || t("Publication started."), { key: "regional-publish" });
  } catch (err) {
    reportError(err as Error);
  }
}
